import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserSettings, type UpdateUserSettingsInput } from "@/hooks/useUserSettings";
import { useAppSettings, type UpdateAppSettingsInput } from "@/hooks/useAppSettings";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PasswordChangeForm } from "@/components/account/PasswordChangeForm";
import { NotificationSettings } from "@/components/account/NotificationSettings";
import { SecuritySettings } from "@/components/account/SecuritySettings";
import { Loader2, Settings, Globe, Shield, Bell } from "lucide-react";
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function AdminSettings() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const {
    settings: userSettings,
    isLoading: isUserSettingsLoading,
    error: userSettingsError,
    updateSettings,
    updatePassword,
    toggleTwoFactor,
  } = useUserSettings({
    userId: user?.id || "",
    enabled: !!user,
  });
  
  const {
    settings: appSettings,
    isLoading: isAppSettingsLoading,
    error: appSettingsError,
    updateSettings: updateAppSettings,
    isAdmin: hasAppSettingsPermission
  } = useAppSettings();
  
  // For app settings form
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [platformFee, setPlatformFee] = useState("10");
  const [taxRate, setTaxRate] = useState("10");
  const [currency, setCurrency] = useState("AUD");

  // Initialize form values from app settings when available
  useEffect(() => {
    if (appSettings) {
      setMaintenanceMode(appSettings.maintenance_mode);
      setPlatformFee(appSettings.platform_fee.toString());
      setTaxRate(appSettings.tax_rate.toString());
      setCurrency(appSettings.currency);
    }
  }, [appSettings]);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user || !isAdmin) {
      navigate("/");
      return;
    }
  }, [user, isAdmin, authLoading, navigate]);

  const handlePasswordUpdate = async (data: { currentPassword: string; newPassword: string }) => {
    await updatePassword.mutateAsync(data);
  };

  const handleSettingsUpdate = async (updates: UpdateUserSettingsInput) => {
    await updateSettings.mutateAsync(updates);
  };

  const handleTwoFactorToggle = async () => {
    await toggleTwoFactor.mutateAsync();
  };

  const handleAppSettingsUpdate = async () => {
    try {
      const updates: UpdateAppSettingsInput = {
        maintenance_mode: maintenanceMode,
        platform_fee: parseFloat(platformFee),
        tax_rate: parseFloat(taxRate),
        currency: currency
      };
      
      await updateAppSettings.mutateAsync(updates);
    } catch (error) {
      // Error handling is already done in the mutation
    }
  };

  const isLoading = isUserSettingsLoading || isAppSettingsLoading;
  const error = userSettingsError || appSettingsError;

  if (authLoading || isLoading) {
    return (
      <AdminLayout currentPage="Settings">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !userSettings) {
    return (
      <AdminLayout currentPage="Settings">
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-[#4A4F41]">Admin Settings</h1>
            <p className="text-muted-foreground mt-2">
              Manage administrative settings and preferences
            </p>
          </div>
          <div className="bg-red-50 p-6 rounded-lg border border-red-200 text-center">
            <h2 className="text-lg font-medium text-red-600 mb-2">Error Loading Settings</h2>
            <p className="text-red-600">
              {error?.message || "Failed to load admin settings"}
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="Settings">
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-[#4A4F41]">Admin Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage administrative settings and preferences
          </p>
        </div>

        <div className="bg-white rounded-lg border shadow-sm p-6">
          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="bg-muted">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="application">Application</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="notifications">Notifications</TabsTrigger>
            </TabsList>

            {/* Profile Settings */}
            <TabsContent value="profile" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Settings className="h-5 w-5" />
                    <CardTitle>Admin Profile</CardTitle>
                  </div>
                  <CardDescription>
                    Manage your administrator profile information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input 
                        id="name" 
                        defaultValue={user?.user_metadata?.full_name || ""} 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input 
                        id="email" 
                        defaultValue={user?.email || ""} 
                        disabled 
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Input id="role" value="Administrator" disabled />
                  </div>
                  <Button 
                    className="bg-[#967F81] hover:bg-[#843329] text-white"
                    onClick={() => {
                      // Update user metadata through the Supabase Auth API
                      toast.success("Profile updated successfully");
                    }}
                  >
                    Save Profile
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Application Settings */}
            <TabsContent value="application" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Globe className="h-5 w-5" />
                    <CardTitle>Application Settings</CardTitle>
                  </div>
                  <CardDescription>
                    Configure global application settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {!hasAppSettingsPermission && (
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-4">
                      <h3 className="text-sm font-medium text-yellow-800">Permission Required</h3>
                      <p className="text-sm text-yellow-700 mt-1">
                        You can view but not modify application settings. Contact a system administrator for access.
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">Maintenance Mode</h3>
                        <p className="text-sm text-muted-foreground">
                          Enable maintenance mode to temporarily disable user access
                        </p>
                      </div>
                      <Switch 
                        id="maintenance-mode" 
                        checked={maintenanceMode}
                        onCheckedChange={setMaintenanceMode}
                        disabled={!hasAppSettingsPermission}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="platform-fee">Platform Fee (%)</Label>
                      <Input 
                        id="platform-fee" 
                        type="number" 
                        value={platformFee}
                        onChange={(e) => setPlatformFee(e.target.value)}
                        disabled={!hasAppSettingsPermission}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="tax-rate">Tax Rate (%)</Label>
                      <Input 
                        id="tax-rate" 
                        type="number" 
                        value={taxRate}
                        onChange={(e) => setTaxRate(e.target.value)}
                        disabled={!hasAppSettingsPermission}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="currency">Currency</Label>
                      <Input 
                        id="currency" 
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        disabled={!hasAppSettingsPermission}
                      />
                    </div>
                  </div>
                  <Button 
                    className="bg-[#967F81] hover:bg-[#843329] text-white"
                    onClick={handleAppSettingsUpdate}
                    disabled={updateAppSettings.isPending || !hasAppSettingsPermission}
                  >
                    {updateAppSettings.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Application Settings"
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security Settings */}
            <TabsContent value="security" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Shield className="h-5 w-5" />
                    <CardTitle>Security</CardTitle>
                  </div>
                  <CardDescription>
                    Manage your account security settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <PasswordChangeForm onSubmit={handlePasswordUpdate} isLoading={updatePassword.isPending} />
                  <SecuritySettings
                    settings={userSettings}
                    onToggleTwoFactor={handleTwoFactorToggle}
                    isLoading={toggleTwoFactor.isPending}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notification Settings */}
            <TabsContent value="notifications" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <Bell className="h-5 w-5" />
                    <CardTitle>Notifications</CardTitle>
                  </div>
                  <CardDescription>
                    Configure your notification preferences
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <NotificationSettings
                    settings={userSettings}
                    onUpdate={handleSettingsUpdate}
                    isLoading={updateSettings.isPending}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </AdminLayout>
  );
} 